import { useEffect, useRef } from 'react';

import { useCompany } from './company';
import { onPendingPurchaseApproved, reportApprovedPendingPurchase } from '../services/purchase';

/** Bounded retry for an approved purchase no destination has taken yet. */
export const PURCHASE_RETRY_ATTEMPTS = 5;
export const PURCHASE_RETRY_INTERVAL_MS = 1000;

/**
 * Sends a purchase that was approved but whose event never left the browser.
 *
 * It lives above the routes on purpose. The PIX modal only flags the approval and closes two
 * seconds later, so a retry owned by it would be cancelled with it; here the attempts survive the
 * modal, and a snapshot left behind by a reload is picked up on the next boot.
 */
export const usePendingPurchaseRecovery = (): void => {
  const { company } = useCompany();
  const timer = useRef<ReturnType<typeof setInterval> | undefined>();

  useEffect(() => {
    if (!company?.id) return undefined;

    const stop = () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = undefined;
      }
    };

    const attempt = (): boolean => {
      const { ga4Enqueued, gtmEnqueued, metaEnqueued } = reportApprovedPendingPurchase(company);

      return ga4Enqueued || gtmEnqueued || metaEnqueued;
    };

    const run = () => {
      stop();

      if (attempt()) return;

      // Usually the tracking tags are still loading. A handful of attempts covers that; anything
      // beyond it waits for the next boot rather than polling forever.
      let attempts = 0;

      timer.current = setInterval(() => {
        attempts += 1;

        if (attempt() || attempts >= PURCHASE_RETRY_ATTEMPTS) {
          stop();
        }
      }, PURCHASE_RETRY_INTERVAL_MS);
    };

    // On mount for a snapshot left behind by a reload, and on approval for the one paid right now.
    run();

    const unsubscribe = onPendingPurchaseApproved(run);

    return () => {
      unsubscribe();
      stop();
    };
  }, [company]);
};
